/**
 * The route contract — the single source of truth for the HTTP surface.
 *
 * The Worker registers handlers from this table, the typed client is generated from it,
 * and the Swift/Kotlin DTOs come from the same schemas. A route cannot exist on the
 * server without existing here, and a client cannot call one that doesn't.
 */

import { z } from 'zod';
import { AnalyticsValueSchema } from './analytics.js';
import {
  AchievementSchema,
  AppealResultSchema,
  CardStateSchema,
  CefrDetailSchema,
  CertificateSchema,
  ClinicSchema,
  ConjugationGridSchema,
  ContentManifestSchema,
  ContentReportAckSchema,
  ContentReportListSchema,
  CorrectionSchema,
  CourseCatalogueSchema,
  CoursePathSchema,
  CourseSchema,
  DismissalGiftResultSchema,
  DrillResultsSchema,
  DrillSchema,
  EntitlementSchema,
  ExportStatusSchema,
  GrammarConceptSchema,
  IngestedSchema,
  ItemDetailSchema,
  LeagueSchema,
  LessonDetailSchema,
  LessonSummarySchema,
  ListeningItemSchema,
  MistakeSchema,
  ProgressHomeSchema,
  ReferralRedemptionSchema,
  ReferralSchema,
  RefundOutcomeSchema,
  ReportCardSchema,
  ReviewGradeSchema,
  ReviewQueueSchema,
  ScenarioSchema,
  SessionSchema,
  SessionStartSchema,
  TodaySchema,
  TurnSchema,
  UsageMeterSchema,
  UserSchema,
  UtteranceFeedbackSchema,
} from './schemas/domain.js';
import {
  CefrSchema,
  ContentIdSchema,
  CorrectionStyleSchema,
  CursorPageSchema,
  DialectSchema,
  DisputeReasonSchema,
  EpochMsSchema,
  InterfaceLocaleSchema,
  LanguageSchema,
  LocalDateSchema,
  ModalitySchema,
  NotificationTypeSchema,
  PersonaIdSchema,
  PillarSchema,
  paginated,
  ScaffoldingSchema,
  Score100Schema,
  SessionKindSchema,
  SessionModeSchema,
  TimezoneSchema,
  UlidSchema,
  Unit01Schema,
} from './schemas/primitives.js';

export const API_VERSION = 'v1';
export const API_BASE_PATH = `/${API_VERSION}`;

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RouteDefinition {
  readonly method: HttpMethod;
  /** Path template with `:param` placeholders, relative to the API base. */
  readonly path: string;
  readonly summary: string;
  readonly params?: z.ZodTypeAny;
  readonly query?: z.ZodTypeAny;
  readonly body?: z.ZodTypeAny;
  readonly response: z.ZodTypeAny;
  /** `anonymous` accepts a device-bound token; `none` is unauthenticated. */
  readonly auth: 'required' | 'anonymous' | 'none';
  /** POSTs that create state honour `Idempotency-Key`. */
  readonly idempotent?: boolean;
  /** Routes whose responses carry an ETag. */
  readonly cacheable?: boolean;
}

/**
 * Identity function that *preserves* the literal argument type. This matters: the Worker
 * reads `routes.x.body` and passes it straight to Zod, so the schema must stay concrete
 * rather than widening to `ZodTypeAny | undefined`.
 */
function route<const T extends RouteDefinition>(def: T): T {
  return def;
}

const Empty = z.object({});
const Ok = z.object({ ok: z.literal(true) });

// ── Auth ──────────────────────────────────────────────────────────────────────

const TokenPairSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresAt: EpochMsSchema,
  user: UserSchema,
});

export const routes = {
  /**
   * Creates a device-bound anonymous account. Identity is deferred until it has obvious
   * value to the learner, so onboarding asks for nothing (docs/02-user-flows.md §2).
   */
  authAnonymous: route({
    method: 'POST',
    path: '/auth/anonymous',
    summary: 'Create a device-bound anonymous account',
    auth: 'none',
    idempotent: true,
    body: z.object({
      deviceId: z.string().min(8).max(128),
      platform: z.enum(['ios', 'android', 'web']),
      appVersion: z.string().max(32),
      osVersion: z.string().max(64).optional(),
      locale: InterfaceLocaleSchema.optional(),
      timezone: TimezoneSchema.optional(),
    }),
    response: TokenPairSchema,
  }),

  /** Links a real identity to the current (possibly anonymous) account. Idempotent merge. */
  authLink: route({
    method: 'POST',
    path: '/auth/link',
    summary: 'Link Apple/Google/passkey/email to the current account',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      provider: z.enum(['apple', 'google', 'passkey', 'email']),
      credential: z.string().min(1),
      email: z.string().email().optional(),
    }),
    response: TokenPairSchema,
  }),

  /**
   * Starts a passkey ceremony, for registration or for sign-in.
   *
   * The challenge is the server's, is single-use, and expires: a client that could
   * choose its own challenge could replay a signature forever. `auth: 'anonymous'`
   * because registering a passkey happens on the account the learner already has, and
   * signing in with one happens before they have any.
   */
  authPasskeyChallenge: route({
    method: 'POST',
    path: '/auth/passkey/challenge',
    summary: 'Begin a passkey registration or sign-in ceremony',
    auth: 'anonymous',
    body: z.object({ intent: z.enum(['register', 'authenticate']) }),
    response: z.object({
      challenge: z.string().min(16),
      rpId: z.string().min(1),
      /** Credential ids this account already has, so an authenticator can pick one. */
      allowCredentials: z.array(z.string()),
      expiresAt: EpochMsSchema,
    }),
  }),

  /** Stores a new passkey against the current account. */
  authPasskeyRegister: route({
    method: 'POST',
    path: '/auth/passkey/register',
    summary: 'Finish registering a passkey',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      credentialId: z.string().min(1),
      clientDataJSON: z.string().min(1),
      attestationObject: z.string().min(1),
      label: z.string().min(1).max(60),
    }),
    response: z.object({ credentialId: z.string(), label: z.string() }),
  }),

  /**
   * Signs in with a passkey the learner already registered.
   *
   * `auth: 'none'`: this is how a learner on a new device gets a session at all, which
   * is the entire point of the feature.
   */
  authPasskeyLogin: route({
    method: 'POST',
    path: '/auth/passkey/login',
    summary: 'Sign in with a passkey',
    auth: 'none',
    body: z.object({
      credentialId: z.string().min(1),
      clientDataJSON: z.string().min(1),
      authenticatorData: z.string().min(1),
      signature: z.string().min(1),
    }),
    response: TokenPairSchema,
  }),

  authRefresh: route({
    method: 'POST',
    path: '/auth/refresh',
    summary: 'Rotate the refresh token',
    auth: 'none',
    body: z.object({ refreshToken: z.string().min(1) }),
    response: TokenPairSchema,
  }),

  authLogout: route({
    method: 'POST',
    path: '/auth/logout',
    summary: 'Revoke the current refresh-token family',
    auth: 'anonymous',
    body: Empty,
    response: Ok,
  }),

  // ── Referrals ───────────────────────────────────────────────────────────────

  /**
   * This learner's own code, and what it has earned.
   *
   * `auth: 'anonymous'` because the whole product works without an account, and a
   * referral the learner cannot share until they sign up is a referral nobody shares.
   */
  getReferral: route({
    method: 'GET',
    path: '/referral',
    summary: 'Your referral code and its standing',
    auth: 'anonymous',
    response: ReferralSchema,
  }),

  /**
   * Redeems somebody else's code.
   *
   * Idempotent, and refused for an account that has already been referred: a learner
   * can be referred once, by one person, which is the anti-farming guarantee.
   */
  redeemReferral: route({
    method: 'POST',
    path: '/referral/redeem',
    summary: 'Redeem a referral code',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({ code: z.string().min(4).max(16) }),
    response: ReferralRedemptionSchema,
  }),

  // ── Me ──────────────────────────────────────────────────────────────────────

  getMe: route({
    method: 'GET',
    path: '/me',
    summary: 'Current user profile and privacy switches',
    auth: 'anonymous',
    response: UserSchema,
  }),

  patchMe: route({
    method: 'PATCH',
    path: '/me',
    summary: 'Update profile, locale, timezone and privacy switches',
    auth: 'anonymous',
    body: z.object({
      displayName: z.string().max(40).nullable().optional(),
      interfaceLocale: InterfaceLocaleSchema.optional(),
      nativeLanguage: LanguageSchema.optional(),
      timezone: TimezoneSchema.optional(),
      privacy: z
        .object({
          recordingPolicy: z.enum(['keep', 'keep_30d', 'delete_after_scoring']).optional(),
          trainingOptIn: z.boolean().optional(),
          analyticsOptOut: z.boolean().optional(),
        })
        .optional(),
    }),
    response: UserSchema,
  }),

  /** Real export: JSON plus an audio archive, emailed within 24h. */
  /**
   * Builds a full data export, then hands it back in the app.
   *
   * Open to anonymous accounts, which is nearly all of them. It was `required` because
   * the export was going to be emailed, and emailing it was the only delivery — so the
   * one thing that makes an export a real trust signal was unavailable to almost
   * everybody, and the queue consumer behind it was a `console.warn` that sent nothing
   * to anyone. The file is now built, stored and downloaded through `getExport`; an
   * email, when there is an address to use, is a notification rather than the delivery.
   */
  exportMe: route({
    method: 'POST',
    path: '/me/export',
    summary: 'Request a full data export',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({ format: z.enum(['json', 'json+audio', 'anki']).default('json+audio') }),
    response: z.object({ requestId: UlidSchema, estimatedReadyAt: EpochMsSchema }),
  }),

  /**
   * Collects the export, or says it is not ready.
   *
   * The learner's own data, under their own token: the request id is not a capability,
   * because a link that is its own permission is a link that leaks in a browser history.
   */
  getExport: route({
    method: 'GET',
    path: '/me/export/:requestId',
    summary: 'Download a prepared data export',
    auth: 'anonymous',
    params: z.object({ requestId: UlidSchema }),
    response: ExportStatusSchema,
  }),

  /**
   * 30-day soft delete, then a hard delete including the R2 audio.
   *
   * Open to anonymous accounts, which is nearly all of them: this product signs a
   * learner in without asking for anything, so `auth: 'required'` meant the account
   * most people have could not be deleted — the API answered "Sign in to use this",
   * which reads as *make an identity before you may delete your identity*. Deletion is
   * not a privilege above reading and writing the same data, and the same token already
   * does both.
   *
   * The restore link is the part that genuinely needs an email, so the clients say
   * which case the learner is in rather than promising a link nobody can send.
   */
  deleteMe: route({
    method: 'DELETE',
    path: '/me',
    summary: 'Soft-delete the account (30-day restore window)',
    auth: 'anonymous',
    body: z.object({ reason: z.string().max(500).optional() }),
    response: z.object({ deletedAt: EpochMsSchema, hardDeleteAt: EpochMsSchema }),
  }),

  /**
   * Deletes every recording the learner has, now.
   *
   * `docs/10-quality-privacy.md` §5 promises a three-way retention switch *and* bulk
   * delete, and the clients have offered a "Delete my recordings" row from the start
   * with nothing behind it: the nightly job can expire recordings on a 30-day policy,
   * which is not the same as a learner deciding today. The transcripts stay — they are
   * what the report cards and the mistake list are made of, and deleting them would
   * silently delete the learner's progress with them, which is not what the row says.
   */
  deleteRecordings: route({
    method: 'DELETE',
    path: '/me/recordings',
    summary: 'Delete every stored recording for this learner',
    auth: 'anonymous',
    idempotent: true,
    body: Empty,
    response: z.object({
      deleted: z.number().int().nonnegative(),
      /** True when more remain than one call could remove; the client calls again. */
      more: z.boolean(),
    }),
  }),

  /**
   * The notification contract, as the learner sees it.
   *
   * `docs/02-user-flows.md` §8 requires it to be published in-app: never more than two
   * a day, never between 22:00 and 08:00 local, every type individually togglable. A
   * promise the learner cannot read is not a promise, so the limits come back with the
   * toggles rather than living only in our code.
   */
  getNotificationPrefs: route({
    method: 'GET',
    path: '/me/notifications',
    summary: 'Notification toggles and the published limits',
    auth: 'anonymous',
    response: z.object({
      types: z.array(
        z.object({
          type: NotificationTypeSchema,
          muted: z.boolean(),
          title: z.string(),
          /** What this type is for, in the learner's words. */
          detail: z.string(),
        }),
      ),
      limits: z.object({
        maxPerDay: z.number().int().positive(),
        quietFromHour: z.number().int().min(0).max(23),
        quietUntilHour: z.number().int().min(0).max(23),
      }),
      /** Null when no device has given us a push token, which is most of the truth. */
      pushRegisteredAt: EpochMsSchema.nullable(),
    }),
  }),

  setNotificationPref: route({
    method: 'PATCH',
    path: '/me/notifications',
    summary: 'Mute or unmute one notification type',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({ type: NotificationTypeSchema, muted: z.boolean() }),
    response: z.object({ type: NotificationTypeSchema, muted: z.boolean() }),
  }),

  registerDevice: route({
    method: 'POST',
    path: '/devices',
    summary: 'Register or update a push token',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      deviceId: z.string().min(8).max(128),
      platform: z.enum(['ios', 'android', 'web']),
      pushToken: z.string().max(512).nullable(),
      appVersion: z.string().max(32),
      osVersion: z.string().max(64).optional(),
    }),
    response: Ok,
  }),

  // ── Courses ─────────────────────────────────────────────────────────────────

  listCourses: route({
    method: 'GET',
    path: '/courses',
    summary: 'Courses the learner is studying',
    auth: 'anonymous',
    response: z.object({ courses: z.array(CourseSchema) }),
  }),

  createCourse: route({
    method: 'POST',
    path: '/courses',
    summary: 'Start a (language, dialect) course',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      id: UlidSchema,
      language: LanguageSchema,
      dialect: DialectSchema,
      goals: z.array(z.enum(['travel', 'work', 'family', 'exam', 'moving', 'curiosity'])).max(2),
      dailyGoalMinutes: z.number().int().min(1).max(240),
      personaId: PersonaIdSchema,
      personaStrictness: Unit01Schema,
      correctionStyle: CorrectionStyleSchema,
      scaffolding: ScaffoldingSchema,
      reminderLocalTime: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .optional(),
      /**
       * What the learner says their level is, honoured immediately at low confidence.
       *
       * Asking and then ignoring the answer is the single most-repeated pedagogical
       * complaint against the closest competitor (docs/research/03-competitor-pingo.md
       * §4). Held loosely: a wide confidence band means real utterances move it fast.
       */
      selfDeclaredLevel: CefrSchema.optional(),
    }),
    response: CourseSchema,
  }),

  patchCourse: route({
    method: 'PATCH',
    path: '/courses/:courseId',
    summary: 'Update course settings',
    auth: 'anonymous',
    params: z.object({ courseId: UlidSchema }),
    body: z.object({
      rowV: z.number().int().positive(),
      dialect: DialectSchema.optional(),
      dailyGoalMinutes: z.number().int().min(1).max(240).optional(),
      scaffolding: ScaffoldingSchema.optional(),
      persona: z
        .object({
          id: PersonaIdSchema.optional(),
          strictness: Unit01Schema.optional(),
          correctionStyle: CorrectionStyleSchema.optional(),
          speechRate: z.number().min(0.5).max(1.5).optional(),
        })
        .optional(),
      examTarget: z
        .enum(['ielts', 'toefl', 'dele', 'delf', 'jlpt', 'hsk', 'topik', 'goethe'])
        .nullable()
        .optional(),
      examDate: EpochMsSchema.nullable().optional(),
      isActive: z.boolean().optional(),
    }),
    response: CourseSchema,
  }),

  /** Produces a real CEFR estimate with a confidence band, not a self-report. */
  submitPlacement: route({
    method: 'POST',
    path: '/courses/:courseId/placement',
    summary: 'Submit placement evidence and receive a CEFR estimate',
    auth: 'anonymous',
    idempotent: true,
    params: z.object({ courseId: UlidSchema }),
    body: z.object({
      utteranceIds: z.array(UlidSchema).max(8),
      knownItemIds: z.array(ContentIdSchema).max(200),
      shownItemIds: z.array(ContentIdSchema).max(200),
      grammarAnswers: z
        .array(z.object({ itemId: ContentIdSchema, correct: z.boolean() }))
        .max(40)
        .optional(),
    }),
    response: z.object({
      level: CefrSchema,
      confidence: Unit01Schema,
      bandSublevels: z.number().int(),
      vocabEstimate: z.number().int(),
      weakPhonemes: z.array(z.string()),
      /** Human-readable account of how we got here, shown on the CEFR detail screen. */
      evidence: z.array(z.object({ component: z.string(), score: z.number(), weight: z.number() })),
      planPreview: z.array(LessonSummarySchema).max(5),
    }),
  }),

  /** The entire Today screen in one call. */
  getToday: route({
    method: 'GET',
    path: '/courses/:courseId/today',
    summary: 'Everything the Today screen renders',
    auth: 'anonymous',
    cacheable: true,
    params: z.object({ courseId: UlidSchema }),
    response: TodaySchema,
  }),

  getCoursePath: route({
    method: 'GET',
    path: '/courses/:courseId/path',
    summary: 'Course path with per-node state',
    auth: 'anonymous',
    cacheable: true,
    params: z.object({ courseId: UlidSchema }),
    response: CoursePathSchema,
  }),

  // ── Content ─────────────────────────────────────────────────────────────────

  getLesson: route({
    method: 'GET',
    path: '/lessons/:lessonId',
    summary: 'Lesson intro payload',
    auth: 'anonymous',
    cacheable: true,
    params: z.object({ lessonId: ContentIdSchema }),
    response: LessonDetailSchema,
  }),

  getItem: route({
    method: 'GET',
    path: '/items/:itemId',
    summary: 'Word/item detail, including dialect variants',
    auth: 'anonymous',
    cacheable: true,
    params: z.object({ itemId: ContentIdSchema }),
    response: ItemDetailSchema,
  }),

  listScenarios: route({
    method: 'GET',
    path: '/scenarios',
    summary: 'Scenario library with facets',
    auth: 'anonymous',
    cacheable: true,
    query: CursorPageSchema.extend({
      courseId: UlidSchema,
      q: z.string().max(100).optional(),
      category: z
        .enum(['daily_life', 'work', 'travel', 'health', 'services', 'social', 'family', 'exam'])
        .optional(),
      difficulty: z.enum(['easy', 'medium', 'hard']).optional(),
      cefr: CefrSchema.optional(),
      maxMinutes: z.number().int().optional(),
      rail: z.enum(['for_you', 'trending', 'from_your_mistakes']).optional(),
    }),
    response: paginated(ScenarioSchema),
  }),

  getScenario: route({
    method: 'GET',
    path: '/scenarios/:scenarioId',
    summary: 'Scenario detail with success criteria',
    auth: 'anonymous',
    cacheable: true,
    params: z.object({ scenarioId: ContentIdSchema }),
    response: ScenarioSchema,
  }),

  /** Generated from a free-text brief, moderated before the learner sees a preview. */
  createCustomScenario: route({
    method: 'POST',
    path: '/scenarios/custom',
    summary: 'Generate a role-play scenario from a brief',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      id: UlidSchema,
      courseId: UlidSchema,
      brief: z.string().min(3).max(400),
      difficulty: z.enum(['easy', 'medium', 'hard']).optional(),
    }),
    response: ScenarioSchema,
  }),

  /**
   * Turn link sharing on for a scenario you authored, or off again.
   *
   * `shared: false` revokes: the token is dropped, so the link dies for everyone
   * immediately and is never reissued. The rules are in
   * `packages/core/src/sharing/share-policy.ts`, which all three clients also hold —
   * a scenario that moderation retired can never be shared, and neither can someone
   * else's.
   */
  setScenarioSharing: route({
    method: 'POST',
    path: '/scenarios/:scenarioId/sharing',
    summary: 'Share an authored scenario by link, or revoke the link',
    auth: 'anonymous',
    idempotent: true,
    params: z.object({ scenarioId: ContentIdSchema }),
    body: z.object({ shared: z.boolean() }),
    response: z.object({
      visibility: z.enum(['private', 'link']),
      /** Absent when private. Clients build the URL; the server owns the token. */
      shareToken: z.string().nullable(),
      /** The loop's only feedback to the author. */
      timesRun: z.number().int().nonnegative(),
    }),
  }),

  /**
   * What someone with the link sees before they have an account.
   *
   * The one unauthenticated read in the product (`auth: 'none'`), and the whole
   * acquisition surface: a learner shares a role-play they wrote, and the person who
   * opens it can see what it is before deciding to sign up.
   *
   * Deliberately narrow. No target phrases, no success criteria, and no author user id —
   * `authorLabel` is a display name the author chose. A learner authored this for
   * themselves and sent it to people they picked; a link is not consent to be indexed.
   */
  getSharedScenario: route({
    method: 'GET',
    path: '/shared/:shareToken',
    summary: 'Preview a shared scenario without an account',
    auth: 'none',
    cacheable: true,
    params: z.object({ shareToken: z.string().length(22) }),
    response: z.object({
      scenarioId: ContentIdSchema,
      title: z.string(),
      brief: z.string(),
      yourRole: z.string(),
      theirRole: z.string(),
      language: LanguageSchema,
      dialect: DialectSchema,
      cefr: CefrSchema,
      estimatedMinutes: z.tuple([z.number().int(), z.number().int()]),
      authorLabel: z.string().nullable(),
      timesRun: z.number().int().nonnegative(),
    }),
  }),

  /**
   * What has actually been built for a dialect, counted.
   *
   * Reached during onboarding, before a course exists, so it takes a dialect rather
   * than a course id — the same reason `/speech/first-utterance` does.
   *
   * It exists so the plan-building screen can name real numbers. The recorded
   * competitors fill that moment with concrete outputs — "Designed 180 practical
   * scenarios" — while ours only read the learner's own answers back
   * (docs/research/10-competitor-teardown-2026-10-18.md §5). The difference is whether
   * the pause feels like work being done or like a progress bar.
   */
  courseCatalogue: route({
    method: 'GET',
    path: '/catalogue',
    summary: 'How much published content exists for a dialect',
    auth: 'anonymous',
    cacheable: true,
    query: z.object({ dialect: DialectSchema }),
    response: CourseCatalogueSchema,
  }),

  listListening: route({
    method: 'GET',
    path: '/listening',
    summary: 'Listening library including the weekly drops',
    auth: 'anonymous',
    cacheable: true,
    query: CursorPageSchema.extend({
      courseId: UlidSchema,
      level: z.enum(['beginner', 'intermediate', 'advanced']).optional(),
      freshOnly: z.boolean().optional(),
    }),
    response: paginated(ListeningItemSchema),
  }),

  getListeningItem: route({
    method: 'GET',
    path: '/listening/:itemId',
    summary: 'Listening item with a tappable transcript',
    auth: 'anonymous',
    cacheable: true,
    params: z.object({ itemId: ContentIdSchema }),
    response: ListeningItemSchema,
  }),

  listGrammarConcepts: route({
    method: 'GET',
    path: '/grammar/concepts',
    summary: 'Grammar concept map with mastery',
    auth: 'anonymous',
    query: z.object({ courseId: UlidSchema }),
    response: z.object({ concepts: z.array(GrammarConceptSchema) }),
  }),

  getGrammarConcept: route({
    method: 'GET',
    path: '/grammar/concepts/:conceptId',
    summary: 'Concept lesson with L1 contrast',
    auth: 'anonymous',
    cacheable: true,
    params: z.object({ conceptId: ContentIdSchema }),
    response: GrammarConceptSchema,
  }),

  /** Delta manifest for offline sync. */
  getContentManifest: route({
    method: 'GET',
    path: '/content/manifest',
    summary: 'Content delta manifest since a version',
    auth: 'anonymous',
    cacheable: true,
    query: z.object({ courseId: UlidSchema, since: z.number().int().nonnegative().default(0) }),
    response: ContentManifestSchema,
  }),

  // ── Sessions ────────────────────────────────────────────────────────────────

  /**
   * Starts a session. `id` is client-generated, which makes this idempotent and is the
   * structural fix for the "stuck between lessons" failure mode we found in Verbi.
   */
  createSession: route({
    method: 'POST',
    path: '/sessions',
    summary: 'Start a session (client-supplied ULID = idempotency key)',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      id: UlidSchema,
      courseId: UlidSchema,
      kind: SessionKindSchema,
      refId: ContentIdSchema.nullable(),
      mode: SessionModeSchema,
      scaffolding: ScaffoldingSchema.optional(),
      /**
       * What the learner chose this conversation to be about: a debate topic and side,
       * an interview track. Free text, so it cannot be `refId` — that is a content id
       * with a row behind it.
       */
      subject: z.string().min(1).max(200).nullable().optional(),
    }),
    response: SessionStartSchema,
  }),

  getSession: route({
    method: 'GET',
    path: '/sessions/:sessionId',
    summary: 'Fetch a session, including its resume checkpoint',
    auth: 'anonymous',
    params: z.object({ sessionId: UlidSchema }),
    response: z.object({ session: SessionSchema, turns: z.array(TurnSchema) }),
  }),

  patchSession: route({
    method: 'PATCH',
    path: '/sessions/:sessionId',
    summary: 'Checkpoint, pause, resume or abandon',
    auth: 'anonymous',
    params: z.object({ sessionId: UlidSchema }),
    body: z.object({
      rowV: z.number().int().positive(),
      action: z.enum(['checkpoint', 'pause', 'resume', 'abandon']),
      turnIndex: z.number().int().nonnegative().optional(),
      checkpoint: z.string().max(8192).optional(),
    }),
    response: SessionSchema,
  }),

  /** Idempotent on (sessionId, index), so a flaky network cannot duplicate a turn. */
  submitTurn: route({
    method: 'POST',
    path: '/sessions/:sessionId/turns/:index',
    summary: 'Submit one turn',
    auth: 'anonymous',
    idempotent: true,
    params: z.object({ sessionId: UlidSchema, index: z.coerce.number().int().nonnegative() }),
    body: z.object({
      /** Set for text-mode turns. */
      text: z.string().max(2000).optional(),
      /** R2 key of an already-uploaded recording, for voice turns. */
      audioKey: z.string().max(256).optional(),
      expectedText: z.string().max(500).nullable().optional(),
      durationMs: z.number().int().nonnegative().optional(),
      signal: z
        .object({
          snrDb: z.number(),
          speechRatio: Unit01Schema,
          clipped: z.boolean(),
        })
        .optional(),
      hintUsed: z.boolean().default(false),
      translateUsed: z.boolean().default(false),
    }),
    response: z.object({
      turn: TurnSchema,
      nextTurn: TurnSchema.nullable(),
      usage: UsageMeterSchema,
      /** `unavailable` here means scoring failed and the lesson continued anyway. */
      scoreStatus: z.enum(['scored', 'reduced', 'withheld', 'unavailable', 'pending']),
    }),
  }),

  /** Idempotent: re-posting completion is a no-op, never a double count. */
  completeSession: route({
    method: 'POST',
    path: '/sessions/:sessionId/complete',
    summary: 'Complete a session and get its report card',
    auth: 'anonymous',
    idempotent: true,
    params: z.object({ sessionId: UlidSchema }),
    body: z.object({ durationSeconds: z.number().int().nonnegative().optional() }),
    response: z.object({ report: ReportCardSchema, session: SessionSchema }),
  }),

  getReport: route({
    method: 'GET',
    path: '/sessions/:sessionId/report',
    summary: 'Report card for a completed session',
    auth: 'anonymous',
    params: z.object({ sessionId: UlidSchema }),
    response: ReportCardSchema,
  }),

  // ── Speech ──────────────────────────────────────────────────────────────────

  /** Presigned upload so audio never routes through the Worker body. */
  createAudioUpload: route({
    method: 'POST',
    path: '/speech/upload',
    summary: 'Get a presigned audio upload target',
    auth: 'anonymous',
    body: z.object({
      sessionId: UlidSchema.nullable(),
      contentType: z.enum(['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/wav']),
      bytes: z.number().int().positive().max(8_000_000),
      durationMs: z.number().int().positive().max(120_000),
    }),
    response: z.object({
      uploadUrl: z.string().url(),
      audioKey: z.string(),
      expiresAt: EpochMsSchema,
    }),
  }),

  /** One-shot scoring for drills, placement and the pronunciation clinic. */
  scoreSpeech: route({
    method: 'POST',
    path: '/speech/score',
    summary: 'Score a single utterance',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      id: UlidSchema,
      courseId: UlidSchema,
      audioKey: z.string().max(256),
      expectedText: z.string().max(500).nullable(),
      durationMs: z.number().int().positive(),
      signal: z.object({ snrDb: z.number(), speechRatio: Unit01Schema, clipped: z.boolean() }),
      /** Deep phoneme analysis is a Plus capability; free tier gets sentence-level. */
      wantPhonemeDetail: z.boolean().default(true),
    }),
    response: UtteranceFeedbackSchema,
  }),

  /**
   * Scores the very first utterance, before a course exists.
   *
   * This is the product's whole wedge: a scored utterance in front of the learner
   * inside 75 seconds, with no price mentioned. It cannot go through `scoreSpeech`,
   * which needs a course the learner has not created yet — and the alternative every
   * client reached for instead was a hard-coded number, which is the one thing this
   * product may never do. The dialect comes from the body because that is all the
   * scorer actually needs.
   *
   * Nothing is stored. There is no course to attach an utterance to, no mistake may be
   * opened from it, and the learner was told the score is not kept.
   */
  scoreFirstUtterance: route({
    method: 'POST',
    path: '/speech/first-utterance',
    summary: 'Score an utterance recorded during onboarding',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      id: UlidSchema,
      audioKey: z.string().max(256),
      expectedText: z.string().max(500),
      dialect: DialectSchema,
      durationMs: z.number().int().positive(),
      signal: z.object({ snrDb: z.number(), speechRatio: Unit01Schema, clipped: z.boolean() }),
    }),
    /**
     * The same shape as any other score, so the withheld case is the same code path
     * here as everywhere else. Onboarding is where it matters most: telling a learner
     * who spoke that they said nothing is the false negative this product refuses.
     */
    response: UtteranceFeedbackSchema,
  }),

  /**
   * "I said that right." Withdraws the mistake immediately — the learner is probably
   * correct — and adds the sample to our eval corpus. Our worst failure mode becomes
   * our best data asset (docs/06-ai-architecture.md §4).
   */
  appealScore: route({
    method: 'POST',
    path: '/speech/appeal',
    summary: 'Appeal a pronunciation judgement',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      utteranceId: UlidSchema,
      word: z.string().max(80).optional(),
      phoneme: z.string().max(16).optional(),
      /** Separate, explicit consent at the point of appeal. */
      consentToStore: z.boolean(),
      note: z.string().max(300).optional(),
    }),
    response: AppealResultSchema,
  }),

  // ── Drills & review ─────────────────────────────────────────────────────────

  getDrill: route({
    method: 'GET',
    path: '/drills/:drillId',
    summary: "A drill built from a session's errors",
    auth: 'anonymous',
    params: z.object({ drillId: UlidSchema }),
    response: DrillSchema,
  }),

  /**
   * What the learner typed, and what that closed.
   *
   * The drill was a one-way street: it was fetched, rendered and answered, and the
   * answers went nowhere. The screen said so — *"this screen has no grading control, so
   * a number would be invented"* — and reported `correct: null` for every item, so a
   * mistake could never be passed and `mistakeId`, whose own comment reads "so passing it
   * can close it", was read by no client.
   *
   * The body carries the learner's **answer**, not a verdict about it. The server holds
   * the expected answer and which mistake each exercise practises, and a client that sent
   * `correct: true` could otherwise close any mistake it liked — the same reasoning that
   * keeps receipt validation server-side.
   */
  submitDrillResults: route({
    method: 'POST',
    path: '/drills/:drillId/results',
    summary: 'Grade a finished drill and close the mistakes it passed',
    auth: 'anonymous',
    idempotent: true,
    params: z.object({ drillId: UlidSchema }),
    body: z.object({
      results: z
        .array(
          z.object({
            /** Indexes into the drill's own exercise list, which the server re-reads. */
            order: z.number().int().nonnegative(),
            answer: z.string().max(500),
            elapsedMs: z.number().int().nonnegative().optional(),
          }),
        )
        .min(1)
        .max(24),
    }),
    response: DrillResultsSchema,
  }),

  getReviewQueue: route({
    method: 'GET',
    path: '/review/queue',
    summary: 'Due cards with per-card modality assignment',
    auth: 'anonymous',
    query: z.object({
      courseId: UlidSchema,
      limit: z.number().int().min(1).max(200).default(40),
      allowSpoken: z.boolean().default(true),
    }),
    response: ReviewQueueSchema,
  }),

  /** Batch submission so an offline review session replays cleanly on reconnect. */
  submitReviewGrades: route({
    method: 'POST',
    path: '/review/grade',
    summary: 'Submit a batch of review grades',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      courseId: UlidSchema,
      batchId: UlidSchema,
      grades: z.array(ReviewGradeSchema).min(1).max(200),
    }),
    response: z.object({
      applied: z.number().int().nonnegative(),
      /** Already-applied grades are reported, not re-applied. */
      duplicates: z.number().int().nonnegative(),
      cards: z.array(CardStateSchema),
    }),
  }),

  listMistakes: route({
    method: 'GET',
    path: '/mistakes',
    summary: 'Mistake worklist',
    auth: 'anonymous',
    query: CursorPageSchema.extend({
      courseId: UlidSchema,
      status: z.enum(['open', 'corrected', 'all']).default('open'),
      kind: z
        .enum(['pronunciation', 'grammar', 'vocabulary', 'word_choice', 'register', 'listening'])
        .optional(),
    }),
    response: paginated(MistakeSchema).extend({
      openCount: z.number().int().nonnegative(),
      correctedCount: z.number().int().nonnegative(),
    }),
  }),

  /**
   * The `Practise` button on the mistake worklist.
   *
   * In the contract from the start, called by all three clients, and implemented by
   * nobody: every tap answered 404 on every platform, in a 404 shape indistinguishable
   * from a mistake that does not exist. `pnpm contract:reach` reported it reachable
   * throughout, because it only ever asked whether a client calls a route.
   */
  practiceMistake: route({
    method: 'POST',
    path: '/mistakes/:mistakeId/practice',
    summary: 'Generate targeted practice for one mistake',
    auth: 'anonymous',
    // Creates a drill row, so a double tap must not leave two behind.
    idempotent: true,
    params: z.object({ mistakeId: UlidSchema }),
    body: Empty,
    response: DrillSchema,
  }),

  getPronunciationClinic: route({
    method: 'GET',
    path: '/review/clinic',
    summary: 'Weakest phonemes with minimal-pair drills',
    auth: 'anonymous',
    query: z.object({
      courseId: UlidSchema,
      /**
       * Narrows to one sound and includes audio for its word pairs (E4a).
       *
       * Absent means the whole list with words but no audio: synthesising every word of
       * all eight sounds would be dozens of vendor calls on a single request, and the
       * drill is per-sound anyway.
       */
      phoneme: z.string().max(16).optional(),
    }),
    response: ClinicSchema,
  }),

  // ── Progress ────────────────────────────────────────────────────────────────

  getProgress: route({
    method: 'GET',
    path: '/progress',
    summary: 'Progress home: level, skills, spoken minutes, accuracy',
    auth: 'anonymous',
    query: z.object({
      courseId: UlidSchema,
      window: z.enum(['week', 'month', 'quarter', 'all']).default('week'),
    }),
    response: ProgressHomeSchema,
  }),

  getCefrDetail: route({
    method: 'GET',
    path: '/progress/cefr',
    summary: 'The level estimate, its evidence and its error bars',
    auth: 'anonymous',
    query: z.object({ courseId: UlidSchema }),
    response: CefrDetailSchema,
  }),

  listReports: route({
    method: 'GET',
    path: '/progress/reports',
    summary: 'Report-card archive (7 days free, full history on Plus)',
    auth: 'anonymous',
    query: CursorPageSchema.extend({ courseId: UlidSchema }),
    response: paginated(ReportCardSchema).extend({
      /** True when older entries exist but require Plus — drives the history_7d trigger. */
      truncatedByTier: z.boolean(),
    }),
  }),

  listAchievements: route({
    method: 'GET',
    path: '/achievements',
    summary: 'Tiered achievements, including locked tiers',
    auth: 'anonymous',
    response: z.object({ achievements: z.array(AchievementSchema) }),
  }),

  getCurrentLeague: route({
    method: 'GET',
    path: '/leagues/current',
    summary: "This week's league, ranked on spoken minutes",
    auth: 'required',
    response: LeagueSchema.nullable(),
  }),

  // ── Billing ─────────────────────────────────────────────────────────────────

  getEntitlements: route({
    method: 'GET',
    path: '/entitlements',
    summary: 'Tier, limits and current usage',
    auth: 'anonymous',
    response: z.object({ entitlement: EntitlementSchema, usage: UsageMeterSchema }),
  }),

  /**
   * Claims the one-off grant a dismissed paywall owes a learner who has run out.
   *
   * A mutation rather than a read because it grants minutes and records that it did, and
   * idempotent because the client fires it from a sheet dismissal — the one interaction a
   * learner can repeat by accident. The server decides whether anything is owed; the
   * client may not pass a number (docs/08-monetization.md, research/16 §2).
   */
  claimDismissalGift: route({
    method: 'POST',
    path: '/billing/dismissal-gift',
    summary: 'Claim the one-off voice-minute grant after dismissing a paywall',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      /** Which paywall they closed, for the analytics that say whether this works. */
      trigger: z.string().min(1).max(64),
    }),
    response: DismissalGiftResultSchema,
  }),

  submitReceipt: route({
    method: 'POST',
    path: '/billing/receipt',
    summary: 'Validate a store receipt (idempotent on the original transaction id)',
    auth: 'required',
    idempotent: true,
    body: z.object({
      store: z.enum(['apple', 'google', 'stripe']),
      receipt: z.string().min(1),
      productId: z.string().min(1),
    }),
    response: z.object({ entitlement: EntitlementSchema }),
  }),

  /** Telemetry for the paywall experiment framework. */
  recordPaywallEvent: route({
    method: 'POST',
    path: '/billing/paywall-event',
    summary: 'Record a paywall view/expand/cta/dismiss',
    auth: 'anonymous',
    body: z.object({
      id: UlidSchema,
      trigger: z.string().max(40),
      variant: z.string().max(40),
      outcome: z.enum(['view', 'expand', 'cta', 'purchase', 'dismiss']),
      plan: z.string().max(40).optional(),
      msVisible: z.number().int().nonnegative().optional(),
    }),
    response: Ok,
  }),

  requestRefund: route({
    method: 'POST',
    path: '/billing/refund-request',
    summary: 'Self-serve refund request',
    auth: 'required',
    body: z.object({
      reason: z.enum([
        'not_as_expected',
        'technical_problem',
        'accidental',
        'too_expensive',
        'other',
      ]),
      detail: z.string().max(1000).optional(),
    }),
    response: RefundOutcomeSchema,
  }),

  // ── Feedback ────────────────────────────────────────────────────────────────

  /**
   * In-app route for "the AI said I was wrong". Intercepts the complaint that is
   * Verbi's top App Store theme before it becomes a one-star review.
   */
  submitFeedback: route({
    method: 'POST',
    path: '/feedback',
    summary: 'Report bad feedback, a bug, or a content problem',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      id: UlidSchema,
      kind: z.enum(['bad_scoring', 'bug', 'content_error', 'suggestion', 'other']),
      detail: z.string().max(2000),
      sessionId: UlidSchema.nullable(),
      utteranceId: UlidSchema.nullable(),
      contentId: ContentIdSchema.nullable(),
      includeDiagnostics: z.boolean().default(false),
    }),
    response: z.object({ ticketId: UlidSchema, message: z.string() }),
  }),

  // ── Conjugation ─────────────────────────────────────────────────────────────

  /**
   * A tense × person grid for one verb, with the learner's per-cell mastery.
   *
   * Per-cell is the whole point: "I can do the preterite except for *vosotros*" is a
   * real and common shape of knowledge, and a single per-verb score cannot represent
   * it or target practice at it.
   */
  getConjugation: route({
    method: 'GET',
    path: '/conjugation',
    summary: "A verb's conjugation grid with per-cell mastery",
    auth: 'anonymous',
    query: z.object({
      courseId: UlidSchema,
      /** The infinitive. Looked up by lemma, not by item id, because a learner types a verb. */
      lemma: z.string().min(1).max(80),
    }),
    response: ConjugationGridSchema,
  }),

  submitConjugation: route({
    method: 'POST',
    path: '/conjugation',
    summary: 'Record a conjugation attempt, per cell',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      courseId: UlidSchema,
      lemma: z.string().min(1).max(80),
      mode: z.enum(['typed', 'spoken']),
      cells: z
        .array(
          z.object({ tense: z.string().max(40), person: z.string().max(40), correct: z.boolean() }),
        )
        .min(1)
        .max(60),
    }),
    response: z.object({
      accuracy: Score100Schema,
      /** Cells still worth drilling after this attempt. */
      weakest: z.array(z.object({ tense: z.string(), person: z.string() })),
    }),
  }),

  // ── Placement probe ─────────────────────────────────────────────────────────

  /**
   * The vocabulary probe that sets a starting level, and its scoring.
   *
   * It is served rather than shipped because the word list contains **pseudowords** —
   * native-looking forms that mean nothing, which catch over-claiming. A client that
   * holds the list holds the answer key, and the correction is the only thing making
   * the estimate honest. The web client predates this endpoint and imports the list
   * directly; the mobile clients do not, and neither should anything new.
   */
  getPlacementProbe: route({
    method: 'GET',
    path: '/placement/probe',
    summary: 'The vocabulary probe words for a language',
    auth: 'anonymous',
    query: z.object({ language: LanguageSchema }),
    response: z.object({
      language: LanguageSchema,
      /**
       * Shuffle-stable order, and deliberately *not* marked: which of these are
       * pseudowords is exactly what the client must not know.
       */
      words: z.array(z.object({ id: z.string(), display: z.string() })),
    }),
  }),

  scorePlacementProbe: route({
    method: 'POST',
    path: '/placement/probe',
    summary: 'Score a vocabulary probe into a starting level',
    auth: 'anonymous',
    body: z.object({
      language: LanguageSchema,
      /** The ids the learner claimed to know. An empty list is a valid answer. */
      knownIds: z.array(z.string().max(40)).max(200),
    }),
    response: z.object({
      cefr: CefrSchema,
      vocabEstimate: z.number().int().nonnegative(),
      /**
       * True when enough pseudowords were claimed that the answers carry no
       * information. The UI says so rather than quietly halving the number.
       */
      overclaimed: z.boolean(),
      /**
       * Null, always, and present on purpose: the level is held at low confidence
       * until there are real utterances to judge, and the client must not invent a
       * number for it.
       */
      confidence: Unit01Schema.nullable(),
    }),
  }),

  // ── Daily challenge ─────────────────────────────────────────────────────────

  /**
   * One three-minute mixed set, the same for everyone learning that language that day.
   *
   * "The same for everyone" is what makes it a shared event rather than a generated
   * exercise, so the item selection is seeded from the date and the language rather
   * than from the learner. Resets at the learner's own local midnight, which is why
   * the client sends its date instead of the server inferring one.
   */
  getDailyChallenge: route({
    method: 'GET',
    path: '/challenge',
    summary: "Today's shared challenge set",
    auth: 'anonymous',
    query: z.object({
      courseId: UlidSchema,
      /** The learner's own local date, so midnight means their midnight. */
      localDate: LocalDateSchema,
    }),
    response: z.object({
      /** `expired` carries yesterday's set with its answers, for reading not scoring. */
      state: z.enum(['available', 'complete', 'expired', 'unavailable']),
      localDate: LocalDateSchema,
      estimatedSeconds: z.number().int().nonnegative(),
      items: z.array(
        z.object({
          id: ContentIdSchema,
          modality: ModalitySchema,
          prompt: z.string(),
          /** Present only once the set is complete or expired. */
          answer: z.string().nullable(),
        }),
      ),
      /** Null when the learner is not in a league this week. */
      leaderboard: z
        .array(
          z.object({ displayName: z.string(), correct: z.number().int(), rank: z.number().int() }),
        )
        .nullable(),
    }),
  }),

  submitDailyChallenge: route({
    method: 'POST',
    path: '/challenge',
    summary: "Submit today's challenge answers",
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      courseId: UlidSchema,
      localDate: LocalDateSchema,
      answers: z.array(z.object({ itemId: ContentIdSchema, correct: z.boolean() })).max(20),
    }),
    response: z.object({
      correct: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
      /** Already-submitted sets are reported, not re-scored. */
      alreadySubmitted: z.boolean(),
    }),
  }),

  // ── Certificates ────────────────────────────────────────────────────────────

  /**
   * A dated, shareable level card.
   *
   * Generated server-side so the numbers on it are ours rather than whatever the client
   * happened to have cached, and so a shared image cannot be edited into a claim we did
   * not make. It is SVG rather than the PNG the spec first asked for: a Workers runtime
   * has no canvas, rasterising would mean shipping a WASM encoder for no visual gain,
   * and SVG shares and prints sharp at any size.
   */
  getCertificate: route({
    method: 'GET',
    path: '/certificate',
    summary: "The learner's level card, as SVG, with whether it is earned yet",
    auth: 'anonymous',
    query: z.object({ courseId: UlidSchema }),
    response: CertificateSchema,
  }),

  // ── Saved items ─────────────────────────────────────────────────────────────

  // ── Content disputes ────────────────────────────────────────────────────────

  /**
   * "This item is wrong."
   *
   * Verbi has the button; nothing the learner can observe happens behind it. Ours is
   * contracted to say what it did, because a report with no visible consequence is how
   * you teach people to stop reporting. The rules live in
   * `@saysooner/core` `content/disputes.ts`, ported to Swift and Kotlin with the same
   * cases, so all three clients can predict the answer before the round trip.
   */
  reportContent: route({
    method: 'POST',
    path: '/content/reports',
    summary: 'Report a content item as wrong',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      id: UlidSchema,
      itemId: ContentIdSchema,
      courseId: UlidSchema,
      reason: DisputeReasonSchema,
      /** Free text. The only part of a report the six reasons cannot carry. */
      note: z.string().max(500).nullable(),
    }),
    response: ContentReportAckSchema,
  }),

  /**
   * The learner's own reports, so "I told you about this" is checkable by them rather
   * than only by us. Other learners' reports are not exposed: a report names a
   * disagreement, and whose it is nobody else's business.
   */
  listContentReports: route({
    method: 'GET',
    path: '/content/reports',
    summary: "The learner's own content reports",
    auth: 'anonymous',
    query: z.object({
      courseId: UlidSchema,
      limit: z.coerce.number().int().min(1).max(200).default(100),
    }),
    response: ContentReportListSchema,
  }),

  /**
   * The learner's own collection: words, phrases, whole turns and their own clips.
   *
   * Export is part of the contract rather than an afterthought. "Your data leaves if
   * you want it to" is only a trust signal if it is a button, and CSV and Anki are the
   * two formats a language learner actually has somewhere to put.
   */
  listSaved: route({
    method: 'GET',
    path: '/saved',
    summary: "The learner's saved words, phrases, turns and clips",
    auth: 'anonymous',
    query: z.object({
      courseId: UlidSchema,
      kind: z.enum(['word', 'phrase', 'turn', 'clip']).optional(),
      limit: z.coerce.number().int().min(1).max(200).default(100),
    }),
    response: z.object({
      items: z.array(
        z.object({
          id: ContentIdSchema,
          kind: z.enum(['word', 'phrase', 'turn', 'clip']),
          display: z.string(),
          translation: z.string().nullable(),
          /** Where it was saved from, so the learner can go back to the context. */
          sourceRoute: z.string().nullable(),
          audioUrl: z.string().nullable(),
          createdAt: EpochMsSchema,
        }),
      ),
      total: z.number().int().nonnegative(),
    }),
  }),

  saveItem: route({
    method: 'POST',
    path: '/saved',
    summary: 'Save a word, phrase, turn or clip',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      id: UlidSchema,
      courseId: UlidSchema,
      kind: z.enum(['word', 'phrase', 'turn', 'clip']),
      display: z.string().min(1).max(500),
      translation: z.string().max(500).nullable(),
      sourceRoute: z.string().max(200).nullable(),
      audioKey: z.string().max(200).nullable(),
    }),
    response: z.object({ id: UlidSchema, total: z.number().int().nonnegative() }),
  }),

  unsaveItem: route({
    method: 'DELETE',
    path: '/saved/:savedId',
    summary: 'Remove a saved item',
    auth: 'anonymous',
    params: z.object({ savedId: UlidSchema }),
    response: z.object({ removed: z.boolean(), total: z.number().int().nonnegative() }),
  }),

  exportSaved: route({
    method: 'POST',
    path: '/saved/export',
    summary: 'Export saved items as CSV or an Anki-importable TSV',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      courseId: UlidSchema,
      format: z.enum(['csv', 'anki']),
    }),
    response: z.object({
      format: z.enum(['csv', 'anki']),
      filename: z.string(),
      /** Returned inline: a learner's own collection is small, and a signed URL for it
       * would be one more thing that can expire between the tap and the download. */
      content: z.string(),
      count: z.number().int().nonnegative(),
    }),
  }),

  // ── Product analytics ───────────────────────────────────────────────────────

  /**
   * Batched product events. One route for all 169, because the alternative is 169
   * endpoints or an untyped blob, and the catalogue already types the payloads.
   *
   * Client-generated ULIDs make a retried batch safe to re-send: the Worker drops an
   * id it has already written. `accepted` and `discarded` are reported separately so a
   * client can tell "we took it" from "we took it and threw it away because you opted
   * out", and stop queueing in the latter case rather than retrying forever.
   */
  trackEvents: route({
    method: 'POST',
    path: '/events',
    summary: 'Record a batch of product events',
    auth: 'anonymous',
    idempotent: true,
    body: z.object({
      events: z
        .array(
          z.object({
            // A client-minted event id, which is what makes the batch idempotent. Not
            // content, so not widened to a content id when the others were.
            id: UlidSchema,
            name: z.string().min(1).max(64),
            at: EpochMsSchema,
            /**
             * Validated against the catalogue by the Worker, not by this schema: Zod
             * cannot express "exactly the fields this event declares" across 169
             * events without 169 schemas, and the catalogue is already the one place
             * that knows. The clients get the same guarantee from the type system.
             */
            payload: z.record(z.string().max(40), AnalyticsValueSchema),
          }),
        )
        .min(1)
        .max(50),
    }),
    response: IngestedSchema,
  }),
} as const;

export type Routes = typeof routes;
export type RouteName = keyof Routes;

export type RouteBody<N extends RouteName> = Routes[N] extends { body: infer B }
  ? B extends z.ZodTypeAny
    ? z.infer<B>
    : never
  : never;

export type RouteQuery<N extends RouteName> = Routes[N] extends { query: infer Q }
  ? Q extends z.ZodTypeAny
    ? z.infer<Q>
    : never
  : never;

export type RouteParams<N extends RouteName> = Routes[N] extends { params: infer P }
  ? P extends z.ZodTypeAny
    ? z.infer<P>
    : never
  : never;

export type RouteResponse<N extends RouteName> = z.infer<Routes[N]['response']>;

/** Fills `:param` placeholders. Values are URI-encoded. */
export function buildPath(
  template: string,
  params: Readonly<Record<string, string | number>> = {},
): string {
  return template.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing path parameter "${key}" for "${template}"`);
    }
    return encodeURIComponent(String(value));
  });
}

export const ALL_ROUTES = Object.entries(routes) as readonly (readonly [
  RouteName,
  RouteDefinition,
])[];

/** Unused-import guards for schemas referenced only in type positions above. */
export const _schemaRefs = { CorrectionSchema, ModalitySchema, PillarSchema } as const;
